import { queryStatus } from 'utils/queryStatus';
import { useGetEventTypes } from 'components/hooks/useGetEventTypes';
import { useGetPractitioner } from 'api/practitioners/practitionerHooks';
import { useGetAllOrganisationsAndGroups } from 'components/hooks/useGetAllOrganisationsAndGroups';
import type { GroupOrganisationHashMap } from 'utils/createGroupOrganisationMap';
import { OrganisationResponse } from 'api';
import { EventTypes } from 'types/types';

const useGetCoreDetailsData = (
  isCreating: boolean,
): {
  setMyOrganisation: boolean;
  isPermitted: boolean;
  isPending: boolean;
  isError: boolean;
  organisations?: OrganisationResponse[];
  organisationsInGroups: GroupOrganisationHashMap;
  myOrganisation?: OrganisationResponse;
  showOrganisationDropdown: boolean;
  eventTypes: Partial<EventTypes>;
} => {
  const { status: getPractitionerStatus, data: practitioner } =
    useGetPractitioner();

  const { status: getEventTypesStatus, eventTypes } =
    useGetEventTypes(practitioner);

  const {
    status: getAllOrganisationsStatus,
    organisations: unsortedOrganisations,
    organisationsInGroups,
  } = useGetAllOrganisationsAndGroups();

  const responseStatus = queryStatus(
    getPractitionerStatus,
    getEventTypesStatus,
    getAllOrganisationsStatus,
  );

  // Return empty state during pending/error
  if (responseStatus !== 'success') {
    return {
      isPending: responseStatus === 'pending',
      isError: responseStatus === 'error',
      isPermitted: false,
      organisations: undefined,
      organisationsInGroups,
      myOrganisation: undefined,
      setMyOrganisation: true,
      showOrganisationDropdown: false,
      eventTypes: {},
    };
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const hasMultiOrgs = unsortedOrganisations!.length > 1;
  const showOrganisationDropdown = hasMultiOrgs && isCreating;
  const setMyOrganisation = !hasMultiOrgs && isCreating;

  const organisations = unsortedOrganisations?.sort((a, b) => {
    const nameA = a.name.toUpperCase();
    const nameB = b.name.toUpperCase();
    if (nameA < nameB) {
      return -1;
    }
    if (nameA > nameB) {
      return 1;
    }
    return 0;
  });

  return {
    isPending: false,
    isError: false,
    isPermitted: hasMultiOrgs,
    organisations,
    organisationsInGroups,
    myOrganisation: organisations?.[0],
    setMyOrganisation,
    showOrganisationDropdown,
    eventTypes,
  };
};

export default useGetCoreDetailsData;
